"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { PickList, textOf, type PickOption } from "@/components/ui/searchable-select";

/**
 * The Select / SelectTrigger / SelectValue / SelectContent / SelectItem API, now a searchable list (PickList). Screens
 * keep writing the same markup; the parts below are markers that Select reads to build the list.
 */

type TriggerProps = React.ComponentProps<"button"> & { size?: "sm" | "default" };
type ItemProps = { value: string; disabled?: boolean; className?: string; children?: React.ReactNode; textValue?: string };

function SelectTrigger(_: TriggerProps): React.ReactElement | null {
  return null;
}
function SelectValue(_: { placeholder?: React.ReactNode; className?: string; children?: React.ReactNode }): React.ReactElement | null {
  return null;
}
function SelectContent(_: { children?: React.ReactNode; className?: string; position?: string; align?: string }): React.ReactElement | null {
  return null;
}
function SelectGroup(_: { children?: React.ReactNode; className?: string }): React.ReactElement | null {
  return null;
}
function SelectLabel(_: { children?: React.ReactNode; className?: string }): React.ReactElement | null {
  return null;
}
function SelectItem(_: ItemProps): React.ReactElement | null {
  return null;
}
function SelectSeparator(_: { className?: string }): React.ReactElement | null {
  return null;
}
function SelectScrollUpButton(_: { className?: string }): React.ReactElement | null {
  return null;
}
function SelectScrollDownButton(_: { className?: string }): React.ReactElement | null {
  return null;
}

/** Walks the markup for the trigger, the placeholder and the items (with their group headings) */
function readParts(children: React.ReactNode) {
  let trigger: TriggerProps | null = null;
  let placeholder = "";
  const options: PickOption[] = [];
  const walk = (node: React.ReactNode, group?: string) => {
    React.Children.toArray(node).forEach((child) => {
      if (!React.isValidElement(child)) return;
      const props = child.props as any;
      if (child.type === SelectTrigger) {
        trigger = props;
        walk(props.children, group);
      } else if (child.type === SelectValue) {
        if (props.placeholder !== undefined) placeholder = textOf(props.placeholder);
      } else if (child.type === SelectGroup) {
        const label = React.Children.toArray(props.children).find((c) => React.isValidElement(c) && c.type === SelectLabel) as React.ReactElement<any> | undefined;
        walk(props.children, label ? textOf(label.props.children) : group);
      } else if (child.type === SelectItem) {
        const label = props.textValue || textOf(props.children);
        options.push({ value: String(props.value), label, node: props.children, disabled: !!props.disabled, group });
      } else if (child.type === SelectLabel || child.type === SelectSeparator) {
        // headings are taken by their group
      } else if (props?.children !== undefined) {
        walk(props.children, group);
      }
    });
  };
  walk(children);
  return { trigger: trigger as TriggerProps | null, placeholder, options };
}

function Select({
  value,
  defaultValue,
  onValueChange,
  disabled,
  required,
  name,
  children,
}: {
  value?: string;
  defaultValue?: string;
  // Method syntax, as in the old Select: a handler that takes a narrower type ("CALL" | "EMAIL") is accepted
  onValueChange?(value: string): void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}) {
  const [inner, setInner] = React.useState(defaultValue ?? "");
  const current = value !== undefined ? value : inner;
  const { trigger, placeholder, options } = readParts(children);
  const t = (trigger || {}) as TriggerProps;
  const aria = Object.fromEntries(Object.entries(t).filter(([k]) => k.startsWith("aria-"))) as any;
  return (
    <PickList
      value={current}
      onChange={(v) => {
        if (value === undefined) setInner(v);
        onValueChange?.(v);
      }}
      options={options}
      placeholder={placeholder || "Select…"}
      disabled={disabled || !!t.disabled}
      required={required}
      name={name}
      id={t.id}
      className={cn(t.size === "sm" ? "h-7" : "h-8 sm:h-9", "rounded-lg px-3", t.className)}
      {...aria}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
